import React, { type SyntheticEvent, useState, useRef } from "react";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import useExtraCategoryFilter, {
    type UseExtrasCategoryFilterType,
} from "../../../../hooks/shared/extras/useExtrasCategoryFilter";
import { useBnbSelector } from "../../../../redux/store";
import { BnbLabel, useBnbLabel } from "../../../../utils/labels";
import * as ExtraFilterStyles from "./styles/ExtraFilter";
import * as Combobox from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Combobox";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import type { ExtrasCategoryToFilterType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { useCloseOnOutsideClick } from "../../../../../../../common-deprecated/hooks";
import { CategoryIconComponent } from "./ExtrasCategoryIcons";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { trackExtrasFirstSearch } from "../../../../utils/tracking";
import SrOnly, {
    SrOnlyLabel,
} from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import Spacer from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/Spacer";
import ButtonPrimary from "../../../../../../../common-deprecated/components/aem/toyota/ButtonPrimary";

type ExtraFilterPopupType = {
    filterProps: UseExtrasCategoryFilterType;
};

export const ExtraFilterPopup = (props: ExtraFilterPopupType): JSX.Element => {
    const {
        categories,
        rootCategories,
        onSetTempRootCategoryToFilter,
        onSetTempCategoriesToFilter,
        onApplyExtrasFilter,
        // TODO: this is a function to clear all active Filters, this has not yet been asked for, but chances are it will be in the distant future
        // availableOnlineToFilter,
        // onSetAvailableOnlineToFilter,
    } = props.filterProps;

    const [dropdownOpened, setDropDownOpened] = useState(false);

    const selectRootCategory = (event: SyntheticEvent, rootCategory: ExtrasCategoryToFilterType): void => {
        event.stopPropagation();
        setDropDownOpened(false);

        if (rootCategory.isSelected) return;
        if (rootCategory.id === "allExtras") {
            onSetTempRootCategoryToFilter(null);
            return;
        }
        onSetTempRootCategoryToFilter(rootCategory);
    };

    const selectCategory = (event: SyntheticEvent, selectedCategory: ExtrasCategoryToFilterType): void => {
        event.stopPropagation();
        selectedCategory.isSelected = !selectedCategory.isSelected;
        onSetTempCategoriesToFilter(categories.filter((cat) => cat.isSelected));
    };

    return (
        <ExtraFilterStyles.FilterPopover>
            {/* CategoryFilter */}
            <ExtraFilterStyles.FilterPopoverTitle>
                <BnbLabel label="extrasFilterCategory" />
            </ExtraFilterStyles.FilterPopoverTitle>
            {categories.map((category) => {
                return (
                    !!category.code && (
                        <ExtraFilterStyles.FilterPopoverLine
                            onClick={(event) => selectCategory(event, category)}
                            key={category.id || ""}
                        >
                            <span>
                                <CategoryIconComponent category={category.code} />
                                {`${category.name || ""} (${(category.count || "").toString()})`}
                            </span>
                            {category.isSelected && <Icon variant="close" />}
                        </ExtraFilterStyles.FilterPopoverLine>
                    )
                );
            })}

            {/* RootCategoryFilter */}
            <ExtraFilterStyles.FilterPopoverTitle mt="20px">
                <BnbLabel label="extrasFilterSearchAmong" />
            </ExtraFilterStyles.FilterPopoverTitle>

            <Spacer mb={3}>
                <SrOnly as="label" htmlFor="combobox-label">
                    <BnbLabel label="extrasFilterSearchAmong" />
                </SrOnly>
                <Combobox.Wrapper width={1} onClick={() => setDropDownOpened(!dropdownOpened)}>
                    <Combobox.Input
                        type="text"
                        id="combobox-label"
                        role="combobox"
                        aria-owns="IUAON9Bb"
                        aria-autocomplete="list"
                        aria-expanded={dropdownOpened}
                        data-active-option="option1"
                        aria-activedescendant="option1"
                        readOnly
                        value={rootCategories.find((rootCat) => rootCat.isSelected)?.name || ""}
                    />
                    <Combobox.IconWrapper isDown={dropdownOpened}>
                        <Icon variant="chevronDown" data-trigger="single" />
                    </Combobox.IconWrapper>
                    <Combobox.Listbox id="IUAON9Bb" role="listbox">
                        {rootCategories
                            .filter((r) => !r.isSelected)
                            .map((rootCat) => (
                                <Combobox.Option
                                    key={rootCat.id || ""}
                                    role="option"
                                    id={rootCat.id || ""}
                                    onClick={(e) => selectRootCategory(e, rootCat)}
                                >
                                    {rootCat.name}
                                </Combobox.Option>
                            ))}
                    </Combobox.Listbox>
                </Combobox.Wrapper>
            </Spacer>

            {/* // TODO: Wait until this is available to implement or delete from v2?
            <AvailableOnlineFilter
                availableOnlineToFilter={
                    availableOnlineToFilter
                }
                onSetAvailableOnlineToFilter={
                    onSetAvailableOnlineToFilter
                }
            />*/}

            <ButtonPrimary fullWidth onClick={onApplyExtrasFilter}>
                <BnbLabel label="extrasFilterApply" />
            </ButtonPrimary>
        </ExtraFilterStyles.FilterPopover>
    );
};

const ExtraFilter = (): JSX.Element => {
    const filterProps = useExtraCategoryFilter();
    const filterIsOpen = useBnbSelector((state) => state.extrasFilter.showExtrasFilter);
    const searchString = useBnbSelector((state) => state.extrasFilter.searchString);
    // const availableOnlineToFilter = useBnbSelector(state => state.extrasFilter.availableOnlineToFilter);

    const { categories, rootCategories, onSetSearchString, onToggleExtrasFilter } = filterProps;

    //const isFilteringAvailableOnlineToFilter = availableOnlineToFilter;
    const isFilteringOnrootFilter =
        rootCategories.filter((rootCategory) => rootCategory.isSelected)[0].id !== "allExtras";

    const numFilters = categories.filter((c) => c.isSelected).length + (isFilteringOnrootFilter ? 1 : 0); // + (isFilteringAvailableOnlineToFilter ? 1 : 0);

    const searchField = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const alreadyFiltered = useRef(false);

    const placeholderLabel = useBnbLabel("searchAllExtras", {}, "searchExtras");
    const ariaRemoveSearchLabel = useBnbLabel("bnbA11yButtonSearchRemove");
    useCloseOnOutsideClick(containerRef, onToggleExtrasFilter, !filterIsOpen);
    return (
        <>
            <Flex.Row>
                <Flex.Col width={{ _: 1, md: 1 / 2, lg: 7 / 12 }} mb={4} mt={3} position="relative" ref={containerRef}>
                    <ExtraFilterStyles.FilterToggle onClick={onToggleExtrasFilter}>
                        <IconWrapper size="lg">
                            <Icon variant="sort" />
                        </IconWrapper>
                        {numFilters > 0 && (
                            <ExtraFilterStyles.FilterBadge selected={filterIsOpen}>
                                {numFilters}
                            </ExtraFilterStyles.FilterBadge>
                        )}
                        <BnbLabel label="extrasFilterFilter" />
                    </ExtraFilterStyles.FilterToggle>

                    {filterIsOpen && <ExtraFilterPopup filterProps={filterProps} />}
                </Flex.Col>

                <Flex.Col
                    width={{ _: 1, md: 1 / 2, lg: 5 / 12 }}
                    mb={3}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <ExtraFilterStyles.InputGroup>
                        <SrOnlyLabel htmlFor="extrafilter-search">{placeholderLabel}</SrOnlyLabel>
                        <IconWrapper>
                            <Icon variant="search" />
                            <SrOnly>
                                <BnbLabel label="bnbA11yButtonSearch" />
                            </SrOnly>
                        </IconWrapper>
                        <ExtraFilterStyles.InputGroupPrepend
                            ref={searchField}
                            type="text"
                            value={searchString || ""}
                            placeholder={placeholderLabel}
                            name="extrafilter-search"
                            id="extrafilter-search"
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    searchField.current?.blur();
                                }
                            }}
                            onChange={(event) => {
                                onSetSearchString(event.target.value);
                                if (!alreadyFiltered.current && event.target.value) {
                                    alreadyFiltered.current = true;
                                    trackExtrasFirstSearch();
                                }
                            }}
                        />
                        {searchString && (
                            <ExtraFilterStyles.ResetButton
                                type="button"
                                aria-label={ariaRemoveSearchLabel}
                                onClick={(event) => {
                                    event.preventDefault();
                                    onSetSearchString("");

                                    if (searchField.current) {
                                        searchField.current.focus();
                                    }
                                }}
                            >
                                <Icon variant="close" />
                            </ExtraFilterStyles.ResetButton>
                        )}
                    </ExtraFilterStyles.InputGroup>
                </Flex.Col>
            </Flex.Row>
        </>
    );
};

export default ExtraFilter;
